
import { useState, useCallback, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { getChatCompletion } from '@/lib/chatCompletion';
import { convertTextToSpeechElevenLabs } from '@/lib/elevenLabsTextToSpeech';
import { ChatMessage, ChatState } from '@/types/chat';
import { useAudioPlayer } from './useAudioPlayer';
import { useChatManagement } from './useChatManagement';
import { useOpenAIClient } from './useOpenAIClient';
import { useProfile } from '@/hooks/useProfile';

export const useOpenAIAssistant = () => {
  const [state, setState] = useState<ChatState>({
    openai: null,
    isProcessing: false,
    isSpeaking: false,
    response: '',
    conversationHistory: []
  });
  
  const [requestCount, setRequestCount] = useState(0);
  const [userPlan, setUserPlan] = useState<'FREE' | 'UNLIMITED'>('FREE');
  
  const { toast } = useToast();
  const { audioRef, cleanupAudio, playAudio } = useAudioPlayer();
  const { fetchUserProfile, saveChat } = useChatManagement();
  const { initializeOpenAI } = useOpenAIClient();
  const { profile } = useProfile();

  const resetState = useCallback(() => {
    console.log('Resetting state...');
    cleanupAudio();
    setState(prev => ({
      ...prev,
      isProcessing: false,
      isSpeaking: false,
      response: ''
    }));
  }, [cleanupAudio]);

  const processWithGPT = useCallback(async (text: string) => {
    if (!state.openai || !text.trim()) return;

    if (userPlan === 'FREE' && requestCount >= 3) {
      toast({
        title: "Límite alcanzado",
        description: "Has alcanzado el límite de consultas gratuitas. Actualiza tu plan para continuar.",
        variant: "destructive"
      });
      return;
    }

    await cleanupAudio();
    setState(prev => ({ ...prev, isProcessing: true }));

    try {
      console.log('Getting GPT response...');
      const gptResponse = await getChatCompletion(state.openai, text, state.conversationHistory);
      
      if (!gptResponse) {
        throw new Error('No response received from GPT');
      }

      setState(prev => ({
        ...prev,
        response: gptResponse,
        isProcessing: false,
        conversationHistory: [
          ...prev.conversationHistory,
          { role: "user", content: text } as ChatMessage,
          { role: "assistant", content: gptResponse } as ChatMessage
        ]
      }));

      console.log('Converting response to speech using ElevenLabs...');
      setState(prev => ({ ...prev, isSpeaking: true }));
      
      // Get user's preferred voice from profile - use type assertion to access the voice_id property
      // that we just added to the database but isn't reflected in the TypeScript types
      const userVoiceId = profile ? (profile as any).voice_id : undefined;
      console.log('Using voice ID from profile:', userVoiceId || 'default');
      
      const audioData = await convertTextToSpeechElevenLabs(gptResponse, userVoiceId);
      await playAudio(audioData);
      const chatSaved = await saveChat(text, gptResponse);
      
      if (chatSaved) {
        setRequestCount(prev => prev + 1);
      }
      
    } catch (error) {
      console.error('Error in processWithGPT:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Error procesando la solicitud",
        variant: "destructive"
      });
      resetState();
    } finally {
      setState(prev => ({ ...prev, isProcessing: false, isSpeaking: false }));
    }
  }, [state.openai, state.conversationHistory, toast, playAudio, saveChat, cleanupAudio, userPlan, requestCount, resetState, profile]);

  const initClient = useCallback(async () => {
    const client = await initializeOpenAI();
    if (client) {
      setState(prev => ({ ...prev, openai: client }));
    }
  }, [initializeOpenAI]);

  useEffect(() => {
    const loadProfile = async () => {
      const { userPlan: plan, requestCount: count } = await fetchUserProfile();
      // Fix the type issue - ensure we only set valid values for userPlan
      if (plan === 'UNLIMITED' || plan === 'FREE') {
        setUserPlan(plan);
      } else {
        setUserPlan('FREE'); // Default to FREE if invalid plan type
      }
      setRequestCount(count);
    };
    
    loadProfile();
  }, [fetchUserProfile]);

  useEffect(() => {
    return () => {
      console.log('Component unmounting, cleaning up...');
      cleanupAudio();
    };
  }, [cleanupAudio]);

  return {
    initializeOpenAI: initClient,
    processWithGPT,
    isProcessing: state.isProcessing,
    isSpeaking: state.isSpeaking,
    response: state.response,
    requestCount,
    userPlan,
    resetState
  };
};
